// Formatage et classement d'échéances — logique PURE : aucun DOM, aucun réseau.
// Extrait du single-file index.html (l. 477-482, 501-502, 714-720) le 2026-08-05.
//
// Trois bugs de l'original sont corrigés ici, chacun couvert par un test dans
// format.test.js. Ils sont documentés au-dessus de la fonction concernée : ce
// sont des chiffres FAUX affichés à un titulaire, pas des imperfections de style.

/**
 * Vrai si la valeur est un nombre réellement exploitable.
 *
 * Le piège que ce garde-fou ferme : `Number("")` et `Number(" ")` valent **0**,
 * pas NaN. Une garde écrite naïvement en `Number.isFinite(Number(v))` laisse
 * donc une chaîne vide s'afficher comme « 0 € » — un faux zéro, qui se lit comme
 * une donnée mesurée alors que la valeur est absente. C'est exactement le défaut
 * qu'on prétend corriger : il fallait le fermer des deux côtés.
 */
function estNombre(v) {
  if (v == null) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  return Number.isFinite(Number(v));
}

/** Montant en euros, arrondi à l'unité (en-têtes, tuiles, totaux). */
export function eur(n) {
  // CORRIGÉ (B21) : l'original rendait « NaN € » pour toute valeur non numérique.
  // Sur un écran financier, « NaN € » est pire qu'un tiret : il ressemble à une
  // donnée. Tout ce qui n'est pas un nombre fini devient un tiret cadratin.
  if (!estNombre(n)) return "—";
  return Number(n).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

/** Montant en euros avec les centimes (lignes de détail, pièces unitaires). */
export function eur2(n) {
  if (!estNombre(n)) return "—";
  return Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

/** Pourcentage à une décimale. */
export function pctv(v) {
  if (!estNombre(v)) return "—";
  return Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 1 }) + " %";
}

/**
 * Nombre en écriture française (virgule décimale, espace de milliers).
 * Le séparateur décimal par défaut de JavaScript est le point anglais : afficher
 * « 7.7 factures/mois » à un pharmacien français trahit le prototype.
 */
export function nombreFr(n, decimales = 1) {
  if (!estNombre(n)) return "—";
  return Number(n).toLocaleString("fr-FR", { maximumFractionDigits: decimales });
}

/**
 * Écart en points, signe explicite (colonne Δ des alertes).
 * CORRIGÉ (B7) : l'original faisait `Number(r.delta)` sans garde et affichait
 * « NaN pts » quand le champ manquait, « 0 pts » quand il valait null — un faux
 * zéro, qui se lit comme « aucun écart » alors que la valeur est inconnue.
 */
export function deltaPts(v) {
  if (!estNombre(v)) return "—";
  const d = Number(v);
  return (d > 0 ? "+" : "") + d.toLocaleString("fr-FR", { maximumFractionDigits: 1 }) + " pts";
}

/**
 * Parse une date ISO (« 2026-08-05 ») comme une date CALENDAIRE LOCALE.
 *
 * `new Date("2026-08-05")` est interprété par le moteur comme minuit **UTC**,
 * soit 02h00 à Paris en été. Comparer ça à `new Date()` (instant local) fait
 * dériver tous les calculs d'un jour selon l'heure de la journée. C'est la
 * racine de B5 ci-dessous.
 */
export function parseDateLocale(d) {
  if (!d) return null;
  const s = String(d).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) {
    const libre = new Date(d);
    return Number.isNaN(libre.getTime()) ? null : libre;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Date au format français, ou tiret. */
export function fdate(d) {
  const dt = parseDateLocale(d);
  return dt ? dt.toLocaleDateString("fr-FR") : "—";
}

/** Libellé de mois court à partir d'une clé « AAAA-MM ». */
export function moisLbl(m) {
  // CORRIGÉ : l'original faisait `m.split("-")` sans garde. Une seule ligne sans
  // clé de mois faisait remonter une exception qui EFFAÇAIT l'échéancier déjà
  // rendu et laissait trois panneaux vides (B12).
  if (!m || typeof m !== "string") return "—";
  const [y, mo] = m.split("-");
  const noms = ["jan", "fév", "mar", "avr", "mai", "jun", "jul", "aoû", "sep", "oct", "nov", "déc"];
  const nom = noms[Number(mo) - 1];
  if (!nom || !y || y.length < 4) return "—";
  return nom + " " + y.slice(2);
}

/**
 * Classe CSS d'une échéance : dépassée, proche, ou rien.
 *
 * CORRIGÉ (B5) — le bug le plus trompeur du front. L'original comparait
 * `new Date(d)` (minuit UTC) à `new Date()` (instant local) : une facture
 * échéant AUJOURD'HUI ressortait « ech-late », donc peinte en rouge « dépassée ».
 * Et le serveur, lui, la compte comme à venir (`date_echeance >= current_date`,
 * EF l. 122) — le front contredisait le back sur la même facture. Un titulaire
 * se croyait en retard sur des factures dans les temps.
 *
 * `today` est injectable pour que le test soit déterministe (sinon il passerait
 * ou échouerait selon l'heure d'exécution).
 */
export function echClass(d, today = new Date()) {
  const ech = parseDateLocale(d);
  if (!ech) return "";
  const jour = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const jours = Math.round((ech.getTime() - jour.getTime()) / 86400000);
  if (jours < 0) return "ech-late";
  if (jours <= 14) return "ech-soon";
  return "";
}

/**
 * Échappement HTML.
 * CORRIGÉ (B18) : l'apostrophe manquait, alors que `escH` est utilisée en
 * contexte d'attribut JS entre apostrophes (`onclick="loadCout('...')"`).
 * Non exploitable aujourd'hui (les valeurs concernées sont des codes CIP
 * numériques), mais le module notes de frais y injectera des libellés SAISIS.
 */
export function escH(s) {
  return String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
  );
}
